import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Target, Users, CheckCircle, AlertTriangle, Clock, 
  ThumbsUp, ThumbsDown, HelpCircle, MessageSquare, 
  ChevronRight, Lock, Unlock, ArrowRight, Scale,
  GraduationCap, Wrench, Shield, Rocket, Briefcase,
  Undo2, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONES = [
  { id: 'exploration', label: 'Exploration', icon: Target, description: 'Researching options' },
  { id: 'shortlist', label: 'Shortlist', icon: Users, description: 'Narrowed choices' },
  { id: 'lock', label: 'Lock', icon: Lock, description: 'Committed to path' },
  { id: 'apply', label: 'Apply', icon: CheckCircle, description: 'Applications sent' },
  { id: 'accepted', label: 'Accepted', icon: ThumbsUp, description: 'Received acceptance' },
  { id: 'commit', label: 'Commit', icon: Lock, description: 'Deposit paid' },
  { id: 'enroll', label: 'Enroll', icon: GraduationCap, description: 'Officially enrolled' }
];

const AGREEMENT_COLORS = {
  pending: { bg: 'bg-slate-500/20', text: 'text-slate-300', label: 'Pending' },
  student_confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Student Confirmed' },
  parent_support: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Parent Supports' },
  parent_consider: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Under Consideration' },
  parent_not_approved: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Not Approved' },
  agreed: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Family Agreed' },
  conflict: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Conflict' }
};

const PATH_ICONS = {
  college: GraduationCap,
  trade: Wrench,
  military: Shield,
  entrepreneurship: Rocket,
  workforce: Briefcase
};

// Confirmation Dialog
const ConfirmDialog = ({ open, title, message, confirmLabel, onConfirm, onCancel, variant = 'default' }) => {
  if (!open) return null;
  const btnClass = variant === 'danger' 
    ? 'bg-red-600 hover:bg-red-700' 
    : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 bg-[#0a1729]/60 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl"
      >
        <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
        <p className="text-slate-300 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} className="border-slate-600 text-slate-300" data-testid="confirm-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onConfirm} className={btnClass} data-testid="confirm-ok-btn">
            {confirmLabel}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Milestone Timeline
const MilestoneTimeline = ({ currentMilestone, onAdvance, onRevert }) => {
  const currentIndex = MILESTONES.findIndex(m => m.id === currentMilestone);
  
  return (
    <div className="relative">
      <div className="absolute top-6 left-0 right-0 h-1 bg-slate-700">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
          style={{ width: `${(currentIndex / (MILESTONES.length - 1)) * 100}%` }}
        />
      </div>
      
      <div className="flex justify-between relative">
        {MILESTONES.map((milestone, index) => {
          const Icon = milestone.icon;
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isNext = index === currentIndex + 1;
          
          return (
            <div key={milestone.id} className="flex flex-col items-center">
              <button
                onClick={() => isNext && onAdvance(milestone.id)}
                disabled={!isNext}
                data-testid={`milestone-btn-${milestone.id}`}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all z-10 ${
                  isComplete ? 'bg-emerald-500 text-white' :
                  isCurrent ? 'bg-blue-500 text-white ring-4 ring-blue-500/30' :
                  isNext ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 cursor-pointer' :
                  'bg-slate-800 text-slate-300'
                }`}
              >
                {isComplete ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
              </button>
              <p className={`mt-2 text-xs font-medium ${
                isCurrent ? 'text-blue-400' : isComplete ? 'text-emerald-400' : 'text-slate-300'
              }`}>
                {milestone.label}
              </p>
              <p className="text-xs text-slate-300 hidden md:block">{milestone.description}</p>
            </div>
          );
        })}
      </div>
      
      {/* Undo button - shown when not at the first milestone */}
      {currentIndex > 0 && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRevert}
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            data-testid="revert-milestone-btn"
          >
            <Undo2 className="w-4 h-4 mr-1" /> Undo Last Step
          </Button>
        </div>
      )}
    </div>
  );
};

// Path Decision Card
const PathDecisionCard = ({ path, milestone, agreement, onAdvance, onRevert, onParentResponse, onUndoParentResponse, onConfirmPath, onUndoConfirmation }) => {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const PathIcon = PATH_ICONS[path.path_type] || Target;
  const agreementStatus = AGREEMENT_COLORS[agreement?.status] || AGREEMENT_COLORS.pending;
  
  return (
    <GlassCard className="p-6" data-testid={`path-card-${path.id}`}>
      {/* Path Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <PathIcon className="w-7 h-7 text-blue-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{path.institution_name}</h3>
            <p className="text-slate-300 capitalize">{path.path_type} {path.major_or_track ? `\u2022 ${path.major_or_track}` : ''}</p>
          </div>
        </div>
        <Badge className={`${agreementStatus.bg} ${agreementStatus.text} border-none`}>
          {agreementStatus.label}
        </Badge>
      </div>
      
      {/* Milestone Timeline */}
      <div className="mb-8">
        <h4 className="text-white font-medium mb-4">Decision Progress</h4>
        <MilestoneTimeline 
          currentMilestone={milestone?.current_milestone || 'exploration'} 
          onAdvance={onAdvance}
          onRevert={onRevert}
        />
      </div>
      
      {/* Agreement Section */}
      {agreement && (
        <div className="border-t border-slate-700 pt-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Family Agreement
          </h4>
          
          {/* Student Status */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                agreement.student_confirmed ? 'bg-emerald-500/20' : 'bg-slate-700'
              }`}>
                {agreement.student_confirmed ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <HelpCircle className="w-5 h-5 text-slate-300" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">Student</p>
                <p className="text-slate-300 text-sm">
                  {agreement.student_confirmed ? 'Confirmed this path' : 'Not yet confirmed'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {!isParent && !agreement.student_confirmed && (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={onConfirmPath} data-testid="confirm-path-btn">
                  Confirm Path
                </Button>
              )}
              {!isParent && agreement.student_confirmed && (
                <Button size="sm" variant="outline" onClick={onUndoConfirmation} className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10" data-testid="undo-confirm-btn">
                  <Undo2 className="w-3 h-3 mr-1" /> Undo
                </Button>
              )}
            </div>
          </div>
          
          {/* Parent Responses */}
          {agreement.parent_responses?.length > 0 ? (
            <div className="space-y-2">
              {agreement.parent_responses.map((response, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      response.stance === 'support' ? 'bg-emerald-500/20' :
                      response.stance === 'not_approved' ? 'bg-red-500/20' :
                      'bg-amber-500/20'
                    }`}>
                      {response.stance === 'support' ? (
                        <ThumbsUp className="w-5 h-5 text-emerald-400" />
                      ) : response.stance === 'not_approved' ? (
                        <ThumbsDown className="w-5 h-5 text-red-400" />
                      ) : (
                        <HelpCircle className="w-5 h-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{response.parent_name || 'Parent'}</p>
                      <p className="text-slate-300 text-sm capitalize">{response.stance?.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {response.notes && (
                      <p className="text-slate-300 text-sm max-w-xs truncate">{response.notes}</p>
                    )}
                    {isParent && (
                      <Button size="sm" variant="outline" onClick={onUndoParentResponse} className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10" data-testid="undo-parent-response-btn">
                        <Undo2 className="w-3 h-3 mr-1" /> Change
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : isParent && agreement.student_confirmed ? (
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-400 font-medium mb-3">Your child has selected this path. What's your stance?</p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => onParentResponse('support')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="parent-support-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-2" /> Support
                </Button>
                <Button 
                  onClick={() => onParentResponse('consider')}
                  variant="outline"
                  className="border-amber-500 text-amber-400"
                  data-testid="parent-consider-btn"
                >
                  <HelpCircle className="w-4 h-4 mr-2" /> Consider
                </Button>
                <Button 
                  onClick={() => onParentResponse('not_approved')}
                  variant="outline"
                  className="border-red-500 text-red-400"
                  data-testid="parent-not-approved-btn"
                >
                  <ThumbsDown className="w-4 h-4 mr-2" /> Not Approved
                </Button>
              </div>
            </div>
          ) : isParent && !agreement.student_confirmed ? (
            <div className="p-4 rounded-lg bg-slate-800/30 text-center">
              <p className="text-slate-300">Waiting for student to confirm this path selection</p>
            </div>
          ) : !isParent && !agreement.student_confirmed ? (
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-blue-400 mb-3">Ready to commit to this path? Confirm it to let your family know!</p>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={onConfirmPath} data-testid="confirm-path-bottom-btn">
                <CheckCircle className="w-4 h-4 mr-2" /> Confirm This Path
              </Button>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <p className="text-emerald-400">You've confirmed this path. Waiting for parent/guardian input.</p>
              </div>
            </div>
          )}
          
          {/* Conflict Alert */}
          {agreement.has_conflict && (
            <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-medium">Family Conflict Detected</span>
              </div>
              <p className="text-slate-300 text-sm">
                There's a disagreement about this path. Consider using the conflict resolution tools to find a compromise.
              </p>
              <Button className="mt-3 bg-red-600 hover:bg-red-700">
                <Scale className="w-4 h-4 mr-2" /> Start Conflict Resolution
              </Button>
            </div>
          )}
        </div>
      )}
    </GlassCard>
  );
};

// Main Component
export default function DecisionWorkflow() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [milestones, setMilestones] = useState({});
  const [agreements, setAgreements] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  
  const isParent = user?.role === 'parent';
  const studentId = isParent ? user?.linked_children?.[0]?.child_id : user?.id;
  const studentName = isParent ? (user?.linked_children?.[0]?.name || user?.linked_children?.[0]?.child_name) : user?.full_name;
  const parentName = isParent ? user?.full_name : null;
  
  useEffect(() => {
    if (!studentId) return;
    fetchData();
    // Live sync, re-poll every 20s so student sees parent's response (support
    // / consider / not_approved) without needing to refresh manually.
    const t = setInterval(fetchData, 20_000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const pathsEndpoint = isParent && studentId 
        ? `${API}/paths/child/${studentId}` 
        : `${API}/paths/my-paths`;
      
      const [pathsRes, milestonesRes] = await Promise.all([
        axios.get(pathsEndpoint, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/decisions/milestones/${studentId}`, { ...getAuthHeader(), withCredentials: true })
      ]);
      
      setPaths(pathsRes.data.paths || []);
      
      const milestonesMap = {};
      const agreementsMap = {};
      
      for (const m of milestonesRes.data.milestones || []) {
        milestonesMap[m.path_id] = m;
      }
      
      for (const path of pathsRes.data.paths || []) {
        try {
          const agreementRes = await axios.get(`${API}/decisions/agreements/${path.id}`, {
            ...getAuthHeader(), withCredentials: true
          });
          if (agreementRes.data.agreement) {
            agreementsMap[path.id] = agreementRes.data.agreement;
          }
        } catch (e) {}
      }
      
      setMilestones(milestonesMap);
      setAgreements(agreementsMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load decision data');
    } finally {
      setLoading(false);
    }
  };
  
  // Advance milestone with confirmation dialog
  const requestAdvance = (pathId, milestoneId) => {
    const label = MILESTONES.find(m => m.id === milestoneId)?.label || milestoneId;
    setConfirmDialog({
      open: true,
      title: `Advance to "${label}"?`,
      message: `This will move your decision forward to the ${label} stage. You can undo this later if needed.`,
      confirmLabel: `Yes, advance`,
      onConfirm: () => {
        setConfirmDialog({ open: false });
        handleAdvanceMilestone(pathId, milestoneId);
      }
    });
  };
  
  const handleAdvanceMilestone = async (pathId, milestone) => {
    try {
      await axios.post(`${API}/decisions/milestones/${pathId}/advance?milestone=${milestone}`, {}, {
        ...getAuthHeader(), withCredentials: true
      });
      toast.success(`Advanced to ${milestone}`, {
        action: {
          label: 'Undo',
          onClick: () => handleRevertMilestone(pathId)
        }
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to advance milestone');
    }
  };
  
  // Revert milestone with confirmation dialog
  const requestRevert = (pathId) => {
    setConfirmDialog({
      open: true,
      title: 'Undo last step?',
      message: 'This will revert the decision progress to the previous stage.',
      confirmLabel: 'Yes, undo',
      variant: 'danger',
      onConfirm: () => {
        setConfirmDialog({ open: false });
        handleRevertMilestone(pathId);
      }
    });
  };
  
  const handleRevertMilestone = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/milestones/${pathId}/revert`, {}, {
        ...getAuthHeader(), withCredentials: true
      });
      toast.success('Reverted to previous stage');
      fetchData();
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to revert' });
    }
  };
  
  // Confirm path with confirmation dialog
  const requestConfirmPath = (pathId) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm this path?',
      message: 'This signals to your family that you are serious about this path. You can undo this later.',
      confirmLabel: 'Confirm',
      onConfirm: () => {
        setConfirmDialog({ open: false });
        handleConfirmPath(pathId);
      }
    });
  };
  
  const handleConfirmPath = async (pathId) => {
    try {
      // Advance to lock which triggers agreement creation with student_confirmed = true
      await axios.post(`${API}/decisions/milestones/${pathId}/advance?milestone=shortlist`, {}, {
        ...getAuthHeader(), withCredentials: true
      });
      toast.success('Path confirmed! Your family has been notified.', {
        action: {
          label: 'Undo',
          onClick: () => handleUndoConfirmation(pathId)
        }
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm path');
    }
  };
  
  // Undo student confirmation
  const handleUndoConfirmation = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/undo-confirmation`, {}, {
        ...getAuthHeader(), withCredentials: true
      });
      toast.success('Path confirmation undone');
      fetchData();
    } catch (error) {
      toast.error('Failed to undo confirmation');
    }
  };
  
  // Parent response with confirmation dialog
  const requestParentResponse = (pathId, stance) => {
    const stanceLabels = { support: 'Support', consider: 'Consider', not_approved: 'Not Approve' };
    setConfirmDialog({
      open: true,
      title: `${stanceLabels[stance]} this path?`,
      message: `You're about to mark this path as "${stanceLabels[stance]}". You can change your response later.`,
      confirmLabel: stanceLabels[stance],
      variant: stance === 'not_approved' ? 'danger' : 'default',
      onConfirm: () => {
        setConfirmDialog({ open: false });
        handleParentResponse(pathId, stance);
      }
    });
  };
  
  const handleParentResponse = async (pathId, stance) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/parent-response`, {
        stance, notes: ''
      }, { ...getAuthHeader(), withCredentials: true });
      toast.success('Response submitted', {
        action: {
          label: 'Undo',
          onClick: () => handleUndoParentResponse(pathId)
        }
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit response');
    }
  };
  
  // Undo parent response
  const handleUndoParentResponse = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/undo-parent-response`, {}, {
        ...getAuthHeader(), withCredentials: true
      });
      toast.success('Response withdrawn');
      fetchData();
    } catch (error) {
      toast.error('Failed to withdraw response');
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Confirmation Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false })}
        />
        
        {/* Header with Student Identity */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white" data-testid="decision-workflow-title">Decision Workflow</h1>
          <p className="text-slate-300 mt-1">Track path decisions and family agreements</p>
        </div>
        
        {/* Student Identity Banner */}
        <GlassCard className="p-4 flex items-center justify-between" data-testid="student-identity-banner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-lg" data-testid="student-name">
                {isParent ? `${studentName || 'Your Child'}'s Paths` : `${user?.full_name || 'Your'}'s Paths`}
              </p>
              <p className="text-slate-300 text-sm">
                {isParent 
                  ? `You're reviewing as a parent/guardian`
                  : user?.linked_children?.length 
                    ? `Your parent/guardian can see and respond to your choices here`
                    : `Invite a parent/guardian so they can weigh in on your decisions`
                }
              </p>
            </div>
          </div>
          {isParent && user?.linked_children?.length > 1 && (
            <div className="flex gap-2">
              {user.linked_children.map((child, idx) => (
                <Badge key={idx} className={`${child.child_id === studentId ? 'bg-blue-500/30 text-blue-300' : 'bg-slate-700 text-slate-300 cursor-pointer hover:bg-slate-600'}`}>
                  {child.name || child.child_name || `Child ${idx + 1}`}
                </Badge>
              ))}
            </div>
          )}
        </GlassCard>
        
        {/* How It Works, Family Collaboration FIRST */}
        <GlassCard className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-400" />
            How the Decision Workflow Works
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Family Collaboration, shown first */}
            <div className="space-y-3">
              <h4 className="text-purple-400 font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Family Collaboration</h4>
              <div className="text-slate-300 text-sm space-y-2">
                {isParent ? (
                  <>
                    <p><strong className="text-white">Your Role:</strong> Review <strong className="text-blue-300">{studentName || 'your child'}</strong>'s path selections and provide your input.</p>
                    <p><strong className="text-emerald-400">Support:</strong> You fully support this path choice.</p>
                    <p><strong className="text-amber-400">Consider:</strong> You're open but want to discuss more.</p>
                    <p><strong className="text-red-400">Not Approved:</strong> You have concerns about this path.</p>
                    <p className="mt-2 text-white/80">Your input is visible to {studentName || 'your child'} and helps guide the decision.</p>
                  </>
                ) : (
                  <>
                    <p><strong className="text-white">This is a team effort.</strong> Your parent/guardian is part of this process.</p>
                    <p><strong className="text-white">Confirm Paths:</strong> Click "Confirm Path" to signal you're serious about a choice.</p>
                    <p><strong className="text-white">Family Input:</strong> After you confirm, your parent/guardian will see it and respond with their support, questions, or concerns.</p>
                    <p><strong className="text-white">Stay Aligned:</strong> Working together leads to better decisions.</p>
                  </>
                )}
              </div>
            </div>
            
            {/* 7-Stage Journey, shown second */}
            <div className="space-y-3">
              <h4 className="text-blue-400 font-semibold flex items-center gap-2"><Target className="w-4 h-4" /> The 7-Stage Journey</h4>
              <div className="text-slate-300 text-sm space-y-2">
                <p><strong className="text-white">1. Exploration:</strong> Research and discover potential paths.</p>
                <p><strong className="text-white">2. Shortlist:</strong> Narrow down to your top choices.</p>
                <p><strong className="text-white">3. Lock:</strong> Commit to seriously pursuing this path.</p>
                <p><strong className="text-white">4. Apply:</strong> Submit applications.</p>
                <p><strong className="text-white">5. Accepted:</strong> Received an acceptance.</p>
                <p><strong className="text-white">6. Commit:</strong> Make your deposit and confirm.</p>
                <p><strong className="text-white">7. Enroll:</strong> Officially enrolled!</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <p className="text-slate-300 text-sm">
              <strong className="text-amber-400">Tip:</strong> Made a mistake? Every action has an <strong className="text-white">Undo</strong> button. You'll also see a confirmation before any major step so nothing happens by accident.
            </p>
          </div>
        </GlassCard>
        
        {/* Overview Stats */}
        <div className="grid grid-cols-3 gap-4">
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="paths-count">{paths.length}</p>
                <p className="text-slate-300 text-sm">Paths Selected</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="agreed-count">
                  {Object.values(agreements).filter(a => a.status === 'agreed').length}
                </p>
                <p className="text-slate-300 text-sm">Family Agreed</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" data-testid="conflict-count">
                  {Object.values(agreements).filter(a => a.has_conflict).length}
                </p>
                <p className="text-slate-300 text-sm">Conflicts</p>
              </div>
            </div>
          </GlassCard>
        </div>
        
        {/* Path Decision Cards */}
        {paths.length > 0 ? (
          <div className="space-y-6">
            {paths.map((path) => (
              <PathDecisionCard
                key={path.id}
                path={path}
                milestone={milestones[path.id]}
                agreement={agreements[path.id]}
                onAdvance={(m) => requestAdvance(path.id, m)}
                onRevert={() => requestRevert(path.id)}
                onParentResponse={(stance) => requestParentResponse(path.id, stance)}
                onUndoParentResponse={() => handleUndoParentResponse(path.id)}
                onConfirmPath={() => requestConfirmPath(path.id)}
                onUndoConfirmation={() => handleUndoConfirmation(path.id)}
              />
            ))}
          </div>
        ) : (
          <GlassCard className="p-12 text-center" data-testid="no-paths-message">
            <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Paths Selected</h3>
            <p className="text-slate-300 mb-6">
              {isParent 
                ? "Your child hasn't selected any paths yet"
                : "Start by selecting career paths to track your decisions"
              }
            </p>
            {!isParent && (
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Target className="w-4 h-4 mr-2" /> Select Paths
              </Button>
            )}
          </GlassCard>
        )}
      </div>
    </AppLayout>
  );
}
